/* eslint-disable camelcase */
import { formatCurrency } from "@eave/shared/utils/currency";
import { DocTypes, OwnerEntityTypes } from "./documents";
import { MessageShape } from "./messages";
import { DocShape } from ".";

// The shape of the response when querying for the list of existing conditions templates
export interface ConditionTemplateListItem {
  name: string;
  description: string;
}

export interface ConditionTemplate {
  name: string;
  category: string;
  description: string;
  stage: ConditionStage;
  owner: ConditionOwner;
  expected_document_type: string;
  status: ConditionStatus;
  template_text: string;
}

const conditionsAddressFormat = (
  line1: string = "",
  line2: string = "",
  city: string = "",
  state: string = "",
  postalCode: string = "",
) =>
  `${line1 && `${line1}, `}${line2 && `${line2}, `}${city && `${city}, `}${state &&
    `${state}, `}${postalCode && postalCode}`;

export enum ConditionStatus {
  Cleared = "cleared",
  NotApplicable = "not_applicable",
  ReadyToReview = "ready_to_review",
  Received = "received",
  Requested = "requested",
  Open = "open",
}

export enum ConditionStage {
  PreQualification = "pre_qualification",
  PreDecision = "pre_decision",
  PreClosing = "pre_closing",
  AtClosing = "at_closing",
  PostClosing = "post_closing",
}

export enum ConditionOwner {
  Borrower = "borrower",
  ClosingAgent = "closing_agent",
  Lender = "lender",
}

export interface ConditionShape {
  condition: string;
  created_at?: string;
  created_by_email: string;
  created_by_id: string;
  created_by_type: string;
  id: number;
  owner: ConditionOwner;
  requested_date: string;
  received_date: string;
  resolved_at: string;
  resolved_by_email: string;
  resolved_by_id: number;
  stage: ConditionStage;
  status: ConditionStatus;
  status_updated_at: string;
  status_updated_by_id: number;
  status_updated_by_email: string;
  url: string;
  is_document_upload_request: boolean;
  documents?: DocShape[];
  document_type?: DocTypes;
  message_thread_id: string;
  messages?: MessageShape[];
  borrower_comment: string;
  owner_entity_type?: string;
  owner_entity_id?: number;
}

export interface CreateBorrowerCondition {
  condition: string;
  status?: ConditionStatus; // defaults to ConditionStatusRequested on the backend
  stage: ConditionStage;
  is_document_upload_request?: boolean;
  document_type?: DocTypes; // defaults to ConditionAttachment on the backend
  url?: string;
  skipped_document?: boolean;
  owner_entity_type?: string;
  owner_entity_id?: number;
  owner?: ConditionOwner; // defaults to "ConditionOwnerBorrower" on the backend
}

export const settlementAgentCondition = (): CreateBorrowerCondition => ({
  condition: "Add your settlement agent.",
  stage: ConditionStage.PreClosing,
  url: "/settlement_agent?redirect=/dashboard",
  document_type: DocTypes.TitleOther,
  is_document_upload_request: false,
});

export const otherAssetCondition = (assetID: number, amount: number): CreateBorrowerCondition => ({
  condition: `We may require additional documentation or information for your asset in the amount of ${formatCurrency(
    amount.toString(),
  )}.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.AssetOther,
  owner_entity_type: OwnerEntityTypes.Account,
  owner_entity_id: assetID,
  is_document_upload_request: true,
});

export const otherAssetInternalMessage = (description: string) =>
  `Borrower describes this asset as: ${description}`;

export const proceedsFromSaleCondition = (
  reoID: number,
  line1: string,
  line2: string,
  city: string,
  state: string,
  postalCode: string,
): CreateBorrowerCondition => ({
  condition: `Once ${conditionsAddressFormat(
    line1,
    line2,
    city,
    state,
    postalCode,
  )} has sold, please provide the final closing statement.`,
  stage: ConditionStage.PreDecision,
  document_type: DocTypes.ReoFinalSettlementStatement,
  owner_entity_id: reoID,
  owner_entity_type: OwnerEntityTypes.OwnedRealEstate,
  is_document_upload_request: true,
});

export const proceedsFromSaleInternalMessage = (status: string) =>
  `Borrower chose "${status}" as the status of sale as of ${new Date().toLocaleDateString()}.`;

export const assetStatementCondition = (
  assetID: number,
  accountTypeLabel: string,
  institution: string,
  amount: number,
): CreateBorrowerCondition => ({
  condition: `Please upload your two most recent statements to verify the ${accountTypeLabel} account at ${institution} with a balance of ${formatCurrency(
    amount.toString(),
  )}.`,
  stage: ConditionStage.PreQualification,
  is_document_upload_request: true,
  document_type: DocTypes.Statement,
  skipped_document: true,
  owner_entity_type: OwnerEntityTypes.Account,
  owner_entity_id: assetID,
});

export const earnestMoneyCondition = (
  assetID: number,
  amount: number,
): CreateBorrowerCondition => ({
  condition: `Please provide a copy of your canceled check or bank transfer showing payment of an earnest money deposit in the amount of ${formatCurrency(
    amount.toString(),
  )}.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.EarnestMoneyDeposit,
  skipped_document: true,
  owner_entity_type: OwnerEntityTypes.Account,
  owner_entity_id: assetID,
  is_document_upload_request: true,
});

export const earnestMoneyInternalMessage = (amount: number) =>
  `Borrower added earnest money in the amount of ${formatCurrency(amount.toString())}.`;

export const giftFundsConditions = (assetID: number, amount: string): CreateBorrowerCondition[] => [
  {
    condition: `Please provide a signed gift letter for the gift amount of ${formatCurrency(
      amount,
    )} entered on your loan application. Please use the template provided on your dashboard under "Your Documents". This template has specific verbiage that is required.`,
    stage: ConditionStage.PreDecision,
    document_type: DocTypes.GiftLetter,
    owner_entity_type: OwnerEntityTypes.Account,
    owner_entity_id: assetID,
    is_document_upload_request: true,
  },
  {
    condition: `Please tell us how your gift donor will be transferring funds to you. We recommend having them transfer gift funds directly to escrow.`,
    stage: ConditionStage.PreDecision,
    document_type: DocTypes.Statement,
    owner_entity_type: OwnerEntityTypes.Account,
    owner_entity_id: assetID,
    is_document_upload_request: true,
  },
  {
    condition: `Please provide a copy of the check or online bank transaction activity printout showing the source of the gift amount of ${formatCurrency(
      amount,
    )} from the account of the donor.`,
    stage: ConditionStage.PreDecision,
    document_type: DocTypes.GiftFundsEvidence,
    owner_entity_type: OwnerEntityTypes.Account,
    owner_entity_id: assetID,
    is_document_upload_request: true,
  },
];

export const giftFundsInternalMessage = (sourceRelationship: string) =>
  `Donor is borrower's ${sourceRelationship}`;

/**
 *  Income related conditions
 */
export const salariedConditionPaystub = (
  employmentID: number,
  firstName: string,
  employerName: string,
): CreateBorrowerCondition => ({
  condition: `${firstName}, please provide your two most recent paystubs from ${employerName}`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.Paystub,
  owner_entity_type: OwnerEntityTypes.Employment,
  owner_entity_id: employmentID,
  skipped_document: true,
  is_document_upload_request: true,
});

export const salariedConditionW2 = (
  employmentID: number,
  firstName: string,
  employerName: string,
): CreateBorrowerCondition => ({
  condition: `Please upload ${firstName}'s W-2s for ${employerName} in the past two years to verify your employment history and income.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.W2,
  owner_entity_type: OwnerEntityTypes.Employment,
  owner_entity_id: employmentID,
  skipped_document: true,
  is_document_upload_request: true,
});

export const selfEmployedCondition = (
  incomeID: number,
  firstName: string,
  businessName: string,
): CreateBorrowerCondition => ({
  condition: `${firstName}, please provide a statement for ${businessName} detailing earnings, such as an employment contract or a ${new Date().getFullYear()} year to date profit and loss statement.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.IncomeOther,
  skipped_document: true,
  owner_entity_type: OwnerEntityTypes.Employment,
  owner_entity_id: incomeID,
  is_document_upload_request: true,
});

export const covidCondition = (incomeID: number, firstName: string): CreateBorrowerCondition => ({
  condition: `${firstName}, please note your approval is based on being actively employed and your employment status being unaffected by COVID-19.`,
  stage: ConditionStage.PreDecision,
  document_type: DocTypes.IncomeOther,
  owner_entity_type: OwnerEntityTypes.Employment,
  owner_entity_id: incomeID,
  is_document_upload_request: true,
});

export const socialSecurityOnBehalfOfDependentRetirementCondition = (
  incomeID: number,
  firstName: string,
): CreateBorrowerCondition => ({
  condition: `${firstName}, please provide a current bank statement showing that you have received Social Security Income on behalf of a dependent and proof that the income will continue for the next three years.`,
  stage: ConditionStage.PreDecision,
  document_type: DocTypes.IncomeOther,
  skipped_document: true,
  owner_entity_type: OwnerEntityTypes.AdditionalIncome,
  owner_entity_id: incomeID,
});

export const socialSecurityRetirementCondition = (
  incomeID: number,
  firstName: string,
): CreateBorrowerCondition => ({
  condition: `Please upload ${firstName}'s award letter to verify income from Social Security.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.IncomeOther,
  is_document_upload_request: true,
  skipped_document: true,
  owner_entity_type: OwnerEntityTypes.AdditionalIncome,
  owner_entity_id: incomeID,
});

export const pensionOrAnnuityRetirementCondition = (
  incomeID: number,
  firstName: string,
): CreateBorrowerCondition => ({
  condition: `Please upload ${firstName}'s award letter to verify income from Pension or Annuity.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.IncomeOther,
  is_document_upload_request: true,
  skipped_document: true,
  owner_entity_type: OwnerEntityTypes.AdditionalIncome,
  owner_entity_id: incomeID,
});

export const iraDistributionRetirementCondition = (
  incomeID: number,
  firstName: string,
): CreateBorrowerCondition => ({
  condition: `Please upload ${firstName}'s retirement statement to verify income from IRA/401(k) Distribution.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.IncomeOther,
  skipped_document: true,
  owner_entity_type: OwnerEntityTypes.AdditionalIncome,
  owner_entity_id: incomeID,
  is_document_upload_request: true,
});

export const rentalREOCondition = (
  reoID: number,
  line1: string,
  line2: string,
  city: string,
  state: string,
  postalCode: string,
): CreateBorrowerCondition => ({
  condition: `Please provide a signed copy of the current lease for ${conditionsAddressFormat(
    line1,
    line2,
    city,
    state,
    postalCode,
  )}.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.RentalReoLease,
  owner_entity_type: OwnerEntityTypes.OwnedRealEstate,
  owner_entity_id: reoID,
  is_document_upload_request: true,
});

export const otherIncomeCondition = (
  incomeID: number,
  firstName: string,
  amount: number,
): CreateBorrowerCondition => ({
  condition: `${firstName}, please provide a document showing the source of your ${formatCurrency(
    amount.toString(),
  )} monthly income. This document could be an award letter, asset statement or a copy of your divorce decree.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.IncomeOther,
  skipped_document: true,
  owner_entity_type: OwnerEntityTypes.AdditionalIncome,
  owner_entity_id: incomeID,
  is_document_upload_request: true,
});

export const otherIncomeInternalMessage = (description: string, amount: number) =>
  `Borrower describes this income as: ${description} in the amount of ${formatCurrency(
    amount.toString(),
  )} monthly and states that it does not appear on their tax returns.`;

export const fullYearProfitAndLossDocRequest = (
  employerName: string,
  borrowerFirstName: string,
  year: number,
): CreateBorrowerCondition => ({
  condition: `Please upload ${employerName}'s ${year} full year profit and loss statement to verify ${borrowerFirstName}'s business income.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.ProfitAndLossStatement,
  skipped_document: true,
  is_document_upload_request: true,
});

export const completePersonalTaxReturnsDocRequest = (
  borrowerName: string,
  year1: number,
  year2: number,
): CreateBorrowerCondition => ({
  condition: `${borrowerName}, Please upload your ${year1} and ${year2} complete personal tax returns to verify income entered on the loan application.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.Income,
  skipped_document: true,
  is_document_upload_request: true,
});

export const additionalTaxDocumentsForYearCondition = (year: number): CreateBorrowerCondition => ({
  condition: `Please provide any ${year} W-2s, 1099s or K-1s that are available, if you have not already provided them to us.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.Income,
  skipped_document: true,
  is_document_upload_request: true,
});

export const additionalTaxExtensionDocumentsForYearCondition = (
  year: number,
): CreateBorrowerCondition => ({
  condition: `Please provide a copy of your ${year} extension form and any ${year} W-2s, 1099s or K-1s that are available, if you have not already provided them to us.`,
  stage: ConditionStage.PreQualification,
  document_type: DocTypes.Income,
  skipped_document: true,
  is_document_upload_request: true,
});

export const uploadPRCardOrNonPRVisaDocRequest = (
  docType: DocTypes,
  message: string,
): CreateBorrowerCondition => ({
  condition: message,
  stage: ConditionStage.PreQualification,
  document_type: docType,
  skipped_document: true,
  is_document_upload_request: true,
});
